import React, { useState, useRef } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import axios from "axios";
import { isMobile } from "react-device-detect";
import "./Feedback.scss";
import { useToast } from "../../Contexts/ToastContext";
import { notifyProductReviewSubmitted } from "../../utils/productReviewEvents";
import { MobBreadcrumbs } from "../../Account/Mob/MobBreadcrumbs";
import { API_BASE_URL } from '../../config';
import { ensureMediaUrl } from '../../utils/mediaUrl';

const MOB_FEEDBACK_BREADCRUMBS = [
    { label: "Home", path: "/" },
    { label: "My Orders", path: "/orders" },
    { label: "Write feedback", path: null },
];

const WEB_FEEDBACK_BREADCRUMBS = [
    { label: "Home", href: "/" },
    { label: "My Orders", href: "/orders" },
    { label: "Write feedback", isActive: true },
];

function FeedbackBreadcrumbs() {
    if (isMobile) {
        return <MobBreadcrumbs items={MOB_FEEDBACK_BREADCRUMBS} />;
    }

    return (
        <div className="breadcrumbs-wrapper">
            <nav className="breadcrumbs-container breadcrumbs-design-6" aria-label="Breadcrumb">
                {WEB_FEEDBACK_BREADCRUMBS.map((crumb, index) => (
                    <React.Fragment key={crumb.label}>
                        {index > 0 && <span className="breadcrumbs-separator">/</span>}
                        {crumb.isActive ? (
                            <span className="breadcrumbs-current">{crumb.label}</span>
                        ) : (
                            <Link to={crumb.href} className="breadcrumbs-link">{crumb.label}</Link>
                        )}
                    </React.Fragment>
                ))}
            </nav>
        </div>
    );
}

const BASE_URL = API_BASE_URL;
const MAX_IMAGES = 6;

export function MobFeedback() {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const location = useLocation();
    const orderItem = location.state?.orderItem || null;

    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [images, setImages] = useState([]);
    const [previews, setPreviews] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const fileInputRef = useRef(null);

    const handleImageChange = (e) => {
        const newFiles = Array.from(e.target.files || []);
        if (newFiles.length === 0) return;
        setImages((prev) => {
            const merged = [...prev, ...newFiles].slice(0, MAX_IMAGES);
            setPreviews((prevPreviews) => {
                prevPreviews.forEach((u) => URL.revokeObjectURL(u));
                return merged.map((f) => URL.createObjectURL(f));
            });
            return merged;
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const removeImage = (index) => {
        setImages((prev) => prev.filter((_, i) => i !== index));
        setPreviews((prev) => {
            URL.revokeObjectURL(prev[index]);
            return prev.filter((_, i) => i !== index);
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!orderItem?.orderItemId) return;
        if (rating < 1 || rating > 5) {
            showToast({ message: "Please select a rating (1–5 stars)." });
            return;
        }
        setSubmitting(true);
        try {
            const token = localStorage.getItem("token");
            const formData = new FormData();
            formData.append("rating", String(rating));
            formData.append("comment", comment);
            images.forEach((file) => formData.append("images", file));

            await axios.post(
                `${BASE_URL}/reviews/${orderItem.orderItemId}/with-images`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        "Content-Type": "multipart/form-data",
                    },
                }
            );
            notifyProductReviewSubmitted(orderItem.orderItemId);
            setSubmitted(true);
            setTimeout(() => navigate("/orders"), 2000);
        } catch (err) {
            const msg = err.response?.data?.message || err.message || "Failed to submit feedback.";
            showToast({ message: msg });
        } finally {
            setSubmitting(false);
        }
    };

    const handleBack = () => {
        navigate("/orders");
    };

    if (!orderItem) {
        return (
            <div className={`mob-feedback-page${isMobile ? ' mob-orders-scaling-root' : ''}`}>
                <FeedbackBreadcrumbs />
                <div className="mob-feedback-container">
                <div className="mob-feedback-body">
                <div className="mob-feedback-empty">
                    <p>No order item selected. Please go to My Orders and tap &quot;Review&quot; on a delivered item.</p>
                    <button type="button" className="mob-feedback-btn mob-feedback-btn-primary" onClick={() => navigate("/orders")}>
                        Back to Orders
                    </button>
                </div>
                </div>
                </div>
            </div>
        );
    }

    if (submitted) {
        return (
            <div className={`mob-feedback-page${isMobile ? ' mob-orders-scaling-root' : ''}`}>
                <FeedbackBreadcrumbs />
                <div className="mob-feedback-container">
                <div className="mob-feedback-body">
                <div className="mob-feedback-success">
                    <div className="mob-feedback-success-icon">✓</div>
                    <h2>Thank you!</h2>
                    <p>Your feedback has been submitted. Redirecting to orders…</p>
                </div>
                </div>
                </div>
            </div>
        );
    }

    const productImage = orderItem.urls && orderItem.urls.length > 0
        ? ensureMediaUrl(orderItem.urls[0])
        : null;

    return (
        <div className={`mob-feedback-page${isMobile ? ' mob-orders-scaling-root' : ''}`}>
            <FeedbackBreadcrumbs />

            <div className="mob-feedback-container">
            <div className="mob-feedback-body">
            <div className="mob-feedback-product">
                <div className="mob-feedback-product-image">
                    {productImage ? (
                        <img src={productImage} alt={orderItem.title} />
                    ) : (
                        <div className="mob-feedback-product-placeholder">No image</div>
                    )}
                </div>
                <div className="mob-feedback-product-details">
                    <div className="mob-feedback-product-brand">{orderItem.brand}</div>
                    <div className="mob-feedback-product-title">{orderItem.title}</div>
                    {orderItem.size && (
                        <div className="mob-feedback-product-meta">Size: {orderItem.size}</div>
                    )}
                </div>
            </div>

            <form className="mob-feedback-form" onSubmit={handleSubmit}>
                <div className="mob-feedback-field">
                    <label className="mob-feedback-label">Rating *</label>
                    <div className="mob-feedback-stars">
                        {[1, 2, 3, 4, 5].map((n) => (
                            <button
                                key={n}
                                type="button"
                                className={`mob-feedback-star ${rating >= n ? "active" : ""}`}
                                onClick={() => setRating(n)}
                                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                            >
                                ★
                            </button>
                        ))}
                    </div>
                </div>

                <div className="mob-feedback-field">
                    <label className="mob-feedback-label" htmlFor="feedback-comment">
                        Comment (optional)
                    </label>
                    <textarea
                        id="feedback-comment"
                        className="mob-feedback-textarea"
                        value={comment}
                        onChange={(e) => setComment(e.target.value)}
                        rows={4}
                        placeholder="Share your experience with this product…"
                    />
                </div>

                <div className="mob-feedback-field">
                    <label className="mob-feedback-label">Photos (optional, up to {MAX_IMAGES})</label>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleImageChange}
                        className="mob-feedback-file-input"
                        aria-label="Add photos"
                    />
                    <button
                        type="button"
                        className="mob-feedback-upload-trigger"
                        onClick={() => fileInputRef.current?.click()}
                    >
                        Add photos
                    </button>
                    {previews.length > 0 && (
                        <div className="mob-feedback-previews">
                            {previews.map((url, i) => (
                                <div key={i} className="mob-feedback-preview-wrap">
                                    <img src={url} alt={`Preview ${i + 1}`} className="mob-feedback-preview-img" />
                                    <button
                                        type="button"
                                        className="mob-feedback-preview-remove"
                                        onClick={() => removeImage(i)}
                                        aria-label="Remove photo"
                                    >
                                        ×
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                <div className="mob-feedback-actions">
                    <button
                        type="button"
                        className="mob-feedback-btn mob-feedback-btn-secondary"
                        onClick={handleBack}
                        disabled={submitting}
                    >
                        Cancel
                    </button>
                    <button
                        type="submit"
                        className="mob-feedback-btn mob-feedback-btn-primary"
                        disabled={submitting || rating < 1}
                    >
                        {submitting ? "Submitting…" : "Submit feedback"}
                    </button>
                </div>
            </form>
            </div>
            </div>
        </div>
    );
}
